'use client';

import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import PhotoUploadModal from './PhotoUploadModal';
import DocumentUploadModal from './DocumentUploadModal';
import ReviewModal from './ReviewModal';
import CertificationModal from './CertificationModal';
import EmergencyContactModal from './EmergencyContactModal';
import TimeOffRequestModal from './TimeOffRequestModal';
import CompensationEntryModal from './CompensationEntryModal';
import BalanceAllocationModal from './BalanceAllocationModal';
import type {
  Employee,
  EmployeeWithStats,
  EmployeeReview,
  EmployeeCertification,
  EmployeeFullReview,
  EmployeeFullCertification,
  EmployeeEmergencyContact,
  EmployeeAddress,
  EmployeeTimeOffBalance,
  EmployeeCompensationEntry,
  EmployeeDocument,
  SlideoverTab,
} from './types';

interface EmployeeDetailSlideoverProps {
  employee: EmployeeWithStats | null;
  onClose: () => void;
  allEmployees: Employee[];
  parentReviews: EmployeeReview[];
  parentCertifications: EmployeeCertification[];
  onEmployeeChanged?: () => void;
}

const STATUS_DOT_COLORS: Record<string, string> = {
  online: '#16a34a',
  away: '#F5A623',
  offline: '#9ca3af',
};

const AVATAR_GRADIENT = 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)';

export default function EmployeeDetailSlideover({
  employee,
  onClose,
  allEmployees,
  parentReviews,
  parentCertifications,
  onEmployeeChanged,
}: EmployeeDetailSlideoverProps) {
  const { currentUser } = useAuth();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<SlideoverTab>('overview');
  const [editedFields, setEditedFields] = useState<Partial<Employee>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // New modal states
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(null);

  const [reviewModalState, setReviewModalState] = useState<{ open: boolean; existing: EmployeeFullReview | null }>({ open: false, existing: null });
  const [certModalState, setCertModalState] = useState<{ open: boolean; existing: EmployeeFullCertification | null }>({ open: false, existing: null });
  const [contactModalState, setContactModalState] = useState<{ open: boolean; existing: EmployeeEmergencyContact | null }>({ open: false, existing: null });
  const [timeOffModalOpen, setTimeOffModalOpen] = useState(false);
  const [compensationModalOpen, setCompensationModalOpen] = useState(false);
  const [balanceModalState, setBalanceModalState] = useState<{ mode: 'edit' | 'create' | 'grant' | null; existing: EmployeeTimeOffBalance | null }>({ mode: null, existing: null });
  const [terminating, setTerminating] = useState(false);
  const [freshEmployee, setFreshEmployee] = useState<Employee | null>(null);

  const [reviews, setReviews] = useState<EmployeeFullReview[]>([]);
  const [certifications, setCertifications] = useState<EmployeeFullCertification[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<EmployeeEmergencyContact[]>([]);
  const [addresses, setAddresses] = useState<EmployeeAddress[]>([]);
  const [timeOffBalances, setTimeOffBalances] = useState<EmployeeTimeOffBalance[]>([]);
  const [compensationHistory, setCompensationHistory] = useState<EmployeeCompensationEntry[]>([]);
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);

  const updateField = (key: keyof Employee, value: any) => {
    setEditedFields(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const resetEditedFields = useCallback(() => {
    if (!employee) return;
    setEditedFields({});
    setHasUnsavedChanges(false);
  }, [employee]);

  // Resolve avatar_url: if it's a storage path (no http), fetch a signed URL
  useEffect(() => {
    if (!employee?.avatar_url) {
      setResolvedAvatarUrl(null);
      return;
    }
    if (employee.avatar_url.startsWith('http://') || employee.avatar_url.startsWith('https://')) {
      setResolvedAvatarUrl(employee.avatar_url);
      return;
    }
    let cancelled = false;
    fetch('/api/hr/document-download-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ storage_path: employee.avatar_url }),
    })
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.signed_url) setResolvedAvatarUrl(data.signed_url);
      })
      .catch(() => { /* silent fail; will show initials */ });
    return () => { cancelled = true; };
  }, [employee?.avatar_url]);

  useEffect(() => {
    if (!employee) return;
    setActiveTab('overview');
    resetEditedFields();

    const fetchSequentially = async () => {
      const eid = employee.id;

      // Helper: retry on 429 with exponential backoff
      const safeQuery = async <T,>(queryFn: () => Promise<{ data: T | null; error: any }>, label: string): Promise<T[]> => {
        for (let attempt = 0; attempt < 3; attempt++) {
          const { data, error } = await queryFn();
          if (!error) return (data ?? []) as T[];
          const status = String((error as any)?.status ?? (error as any)?.code ?? '');
          const errMsg = (error as any)?.message ?? String(error);
          const isRateLimit = status.includes('429') || errMsg.toLowerCase().includes('rate') || errMsg.toLowerCase().includes('too many');
          console.warn(`[Slideover] ${label} attempt ${attempt + 1} failed (status=${status}):`, errMsg, error);
          if (!isRateLimit) {
            console.warn(`[Slideover] ${label} non-retriable error, returning empty`);
            return [];
          }
          await new Promise(r => setTimeout(r, 200 * Math.pow(2, attempt)));
        }
        console.warn(`[Slideover] ${label} retried 3x and failed`);
        return [];
      };

      // Reviews — fetch full record (parent only has summary)
      const revData = await safeQuery<EmployeeFullReview>(
        () => supabase.from('employee_reviews').select('*').eq('employee_id', eid),
        'reviews'
      );
      setReviews(revData);

      await new Promise(r => setTimeout(r, 150));

      // Certifications — fetch full record
      const certData = await safeQuery<EmployeeFullCertification>(
        () => supabase.from('employee_certifications').select('*').eq('employee_id', eid),
        'certifications'
      );
      setCertifications(certData);

      await new Promise(r => setTimeout(r, 150));

      // Emergency contacts
      const ecData = await safeQuery<EmployeeEmergencyContact>(
        () => supabase.from('employee_emergency_contacts').select('*').eq('employee_id', eid),
        'emergency_contacts'
      );
      setEmergencyContacts(ecData);

      await new Promise(r => setTimeout(r, 150));

      // Addresses
      const addrData = await safeQuery<EmployeeAddress>(
        () => supabase.from('employee_addresses').select('*').eq('employee_id', eid),
        'addresses'
      );
      setAddresses(addrData);

      await new Promise(r => setTimeout(r, 150));

      // Time off balances
      console.log(`[Slideover] Fetching time_off_balances for employee_id: ${eid}`);
      const tobData = await safeQuery<EmployeeTimeOffBalance>(
        () => supabase.from('employee_time_off_balances').select('*').eq('employee_id', eid).eq('is_archived', false),
        'time_off_balances'
      );
      console.log(`[Slideover] time_off_balances fetched:`, tobData.length, 'rows', tobData);
      setTimeOffBalances(tobData);

      await new Promise(r => setTimeout(r, 150));

      // Compensation history
      const compData = await safeQuery<EmployeeCompensationEntry>(
        () => supabase.from('employee_compensation_history').select('*').eq('employee_id', eid),
        'compensation_history'
      );
      setCompensationHistory(compData);

      await new Promise(r => setTimeout(r, 150));

      // Documents
      const docData = await safeQuery<EmployeeDocument>(
        () => supabase.from('employee_documents').select('*').eq('employee_id', eid).eq('is_archived', false),
        'documents'
      );
      setDocuments(docData);
    };

    fetchSequentially();
  }, [employee?.id, resetEditedFields, supabase]);

  // Defensive: re-fetch the employees row whenever the slideover opens, so
  // termination_date / employment_type / updated_at reflect the latest server state
  useEffect(() => {
    if (!employee) {
      setFreshEmployee(null);
      return;
    }
    let cancelled = false;
    supabase.from('employees').select('*').eq('id', employee.id)
      .then(({ data }) => {
        if (!cancelled && data && data.length > 0) {
          setFreshEmployee(data[0] as Employee);
        }
      })
      .catch(err => console.warn('[Slideover] Employee re-fetch failed:', err));
    return () => { cancelled = true; };
  }, [employee?.id, supabase]);

  // Defensive re-fetch: if Time Off tab is opened and balances are empty, re-fetch
  useEffect(() => {
    if (activeTab !== 'time_off') return;
    if (!employee) return;
    if (timeOffBalances.length > 0) return;  // already loaded
    console.log('[Slideover] Time Off tab opened with empty balances — re-fetching');
    supabase.from('employee_time_off_balances')
      .select('*')
      .eq('employee_id', employee.id)
      .eq('is_archived', false)
      .then(({ data, error }) => {
        if (error) {
          console.warn('[Slideover] Time Off re-fetch failed:', error);
        } else if (data) {
          console.log('[Slideover] Time Off re-fetch returned:', data.length, 'rows');
          setTimeOffBalances(data as any);
        }
      });
  }, [activeTab, employee?.id, timeOffBalances.length, supabase]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, submitting, onClose]);

  const handleSave = async () => {
    if (!employee || !hasUnsavedChanges) return;
    setSubmitting(true);

    const cleanPayload: any = { ...editedFields };
    Object.keys(cleanPayload).forEach(k => {
      if ((cleanPayload as any)[k] === (employee as any)[k]) {
        delete cleanPayload[k];
      }
    });
    cleanPayload.updated_at = new Date().toISOString();

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Save timed out')), 10000)
    );
    const savePromise = supabase
      .from('employees')
      .update(cleanPayload)
      .eq('id', employee.id)
      .then(res => {
        if (res.error) throw res.error;
        return res;
      });

    try {
      await Promise.race([savePromise, timeoutPromise]);
      toast.success(`${employee.first_name} ${employee.last_name} updated`, { duration: 3000 });
      setHasUnsavedChanges(false);
      setEditedFields({});
      setSubmitting(false);
      onEmployeeChanged?.();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(`Save failed: ${err instanceof Error ? err.message : 'Unknown'}`);
      setSubmitting(false);
    }
  };

  if (!employee) return null;

  // Use the fresh fetched record if available, fall back to the prop
  const displayEmployee: Employee = (freshEmployee && freshEmployee.id === employee?.id)
    ? freshEmployee
    : (employee as Employee);

  const TABS = [
    { key: 'overview' as SlideoverTab, label: 'Overview', badgeCount: undefined },
    { key: 'documents' as SlideoverTab, label: 'Documents', badgeCount: documents.length || undefined },
    { key: 'reviews' as SlideoverTab, label: 'Reviews', badgeCount: reviews.length || undefined },
    { key: 'certifications' as SlideoverTab, label: 'Certifications', badgeCount: certifications.length || undefined },
    { key: 'time_off' as SlideoverTab, label: 'Time Off', badgeCount: undefined },
    { key: 'compensation' as SlideoverTab, label: 'Compensation', badgeCount: undefined },
  ];

  const dotColor = STATUS_DOT_COLORS[employee.status] ?? STATUS_DOT_COLORS.offline;

  return (
    <div
      className="fixed top-[60px] bottom-0 left-0 right-0 z-[100] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="employee-slideover-title"
    >
      <style jsx>{`
        @keyframes slideoverIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slideover-in {
          animation: slideoverIn 220ms cubic-bezier(0.16, 1, 0.3, 1);
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={submitting ? undefined : onClose}
      />

      {/* Slideover panel */}
      <div
        className="relative bg-white shadow-2xl w-full max-w-2xl h-full overflow-hidden flex flex-col animate-slideover-in"
        onClick={e => e.stopPropagation()}
      >
        {/* ── HEADER ── */}
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-br from-[#E89A3C]/5 to-[#1C2E5E]/5 flex-shrink-0">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-start gap-4 min-w-0 flex-1">
              {/* Clickable avatar with hover overlay */}
              <button
                type="button"
                onClick={() => setPhotoModalOpen(true)}
                className="relative flex-shrink-0 group"
                aria-label="Update profile photo"
              >
                {resolvedAvatarUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={resolvedAvatarUrl}
                    alt=""
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-lg font-bold"
                    style={{ background: AVATAR_GRADIENT }}
                  >
                    {`${employee.first_name?.[0] ?? ''}${employee.last_name?.[0] ?? ''}`.toUpperCase()}
                  </div>
                )}
                <span
                  className="absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white"
                  style={{ background: dotColor }}
                  title={employee.status}
                />
                {/* Hover overlay for "click to edit" */}
                <span className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" strokeLinejoin="round"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                </span>
              </button>

              <div className="min-w-0 flex-1">
                <h3
                  id="employee-slideover-title"
                  className="text-xl font-bold text-[#1C2E5E] truncate"
                >
                  {employee.preferred_name ?? employee.first_name} {employee.last_name}
                </h3>
                {employee.preferred_pronouns && (
                  <p className="text-[11px] text-gray-500">({employee.preferred_pronouns})</p>
                )}
                <p className="text-[13px] text-gray-700 mt-0.5 truncate">{employee.job_title}</p>
                <p className="text-[11px] text-gray-500 truncate">{employee.department}</p>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  {(employee.roles ?? []).slice(0, 4).map(r => (
                    <span
                      key={r}
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700"
                    >
                      {r.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 flex-shrink-0"
              aria-label="Close panel"
            >
              <svg
                className="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Quick contact row */}
          <div className="flex items-center gap-2 flex-wrap">
            {employee.work_email && (
              <a
                href={`mailto:${employee.work_email}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-gray-200 hover:border-[#E89A3C] hover:text-[#E89A3C] transition-colors"
              >
                ✉ {employee.work_email}
              </a>
            )}
            {employee.work_phone && (
              <a
                href={`tel:${employee.work_phone}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-gray-200 hover:border-[#E89A3C] hover:text-[#E89A3C] transition-colors"
              >
                📞 {employee.work_phone}
              </a>
            )}
            {employee.linkedin_url && (
              <a
                href={employee.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-gray-200 hover:border-[#E89A3C] hover:text-[#E89A3C] transition-colors"
              >
                in
              </a>
            )}
            {employee.hire_date && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-gray-200 text-gray-600">
                🗓 Hired{' '}
                {new Date(employee.hire_date).toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>

        {/* ── TABS BAR ── */}
        <div className="flex items-center gap-1 px-4 border-b border-gray-100 overflow-x-auto flex-shrink-0">
          {TABS.map(tab => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative px-3 py-3 text-[12px] font-semibold whitespace-nowrap transition-colors ${
                  active ? 'text-[#E89A3C]' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.badgeCount !== undefined && tab.badgeCount > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] font-bold bg-gray-200 text-gray-700">
                    {tab.badgeCount}
                  </span>
                )}
                {active && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E89A3C] rounded-t" />
                )}
              </button>
            );
          })}
        </div>

        {/* ── TAB CONTENT ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'overview' && (
            <OverviewTab
              employee={employee}
              editedFields={editedFields}
              updateField={updateField}
              resetEditedFields={resetEditedFields}
              hasUnsavedChanges={hasUnsavedChanges}
              submitting={submitting}
              handleSave={handleSave}
              emergencyContacts={emergencyContacts}
              addresses={addresses}
              allEmployees={allEmployees}
              onAddContact={() => setContactModalState({ open: true, existing: null })}
              onEditContact={(ec) => setContactModalState({ open: true, existing: ec })}
            />
          )}
          {activeTab === 'documents' && (
            <DocumentsTab
              documents={documents}
              setDocuments={setDocuments}
              supabase={supabase}
              onUploadClick={() => setDocModalOpen(true)}
            />
          )}
          {activeTab === 'reviews' && (
            <ReviewsTab
              reviews={reviews}
              onNewReview={() => setReviewModalState({ open: true, existing: null })}
              onEditReview={(rev) => setReviewModalState({ open: true, existing: rev })}
            />
          )}
          {activeTab === 'certifications' && (
            <CertificationsTab
              certifications={certifications}
              onNewCert={() => setCertModalState({ open: true, existing: null })}
              onEditCert={(cert) => setCertModalState({ open: true, existing: cert })}
            />
          )}
          {activeTab === 'time_off' && <TimeOffTab timeOffBalances={timeOffBalances} onRequestTimeOff={() => setTimeOffModalOpen(true)} onEditBalance={(bal) => setBalanceModalState({ mode: 'edit', existing: bal })} onGrantBalance={(bal) => setBalanceModalState({ mode: 'grant', existing: bal })} onAddBalance={() => setBalanceModalState({ mode: 'create', existing: null })} />}
          {activeTab === 'compensation' && (
            <CompensationTab compensationHistory={compensationHistory} onAddEntry={() => setCompensationModalOpen(true)} />
          )}
        </div>

        {/* Terminate / Reactivate Footer Bar */}
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-3 flex items-center justify-between flex-shrink-0">
          <p className="text-[10px] text-gray-500">
            {displayEmployee.termination_date
              ? <><span>Terminated </span><span className="font-bold text-red-700">{new Date(displayEmployee.termination_date).toLocaleDateString()}</span>{displayEmployee.termination_reason ? ` — ${displayEmployee.termination_reason}` : ''}</>
              : <><span>Last updated </span><span>{displayEmployee.updated_at ? new Date(displayEmployee.updated_at).toLocaleDateString() : '—'}</span></>}
          </p>
          {displayEmployee.termination_date ? (
            <button
              type="button"
              disabled={terminating}
              onClick={async () => {
                if (!confirm(`Reactivate ${employee.first_name} ${employee.last_name}? This will clear their termination date and set employment type back to full-time.`)) return;
                setTerminating(true);
                console.log('[Termination] Reactivating:', employee.id);

                const updatePayload = {
                  termination_date: null,
                  termination_reason: null,
                  employment_type: 'full_time',
                  updated_at: new Date().toISOString(),
                };

                let success = false;
                for (let attempt = 0; attempt < 3; attempt++) {
                  const timeoutPromise = new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error(`Reactivate timed out (attempt ${attempt + 1}/3)`)), 8000));
                  const opPromise = supabase.from('employees').update(updatePayload).eq('id', employee.id)
                    .then((res: any) => { if (res.error) throw res.error; return res.data; });

                  try {
                    await Promise.race([opPromise, timeoutPromise]);
                    console.log(`[Termination] Reactivate succeeded attempt ${attempt + 1}`);
                    success = true;
                    break;
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
                    const isTimeout = msg.includes('timed out');
                    console.warn(`[Termination] Reactivate attempt ${attempt + 1} failed: ${msg}`);
                    if (attempt === 2 || (!isRateLimit && !isTimeout)) {
                      toast.error(`Reactivate failed: ${msg}`, { duration: 6000 });
                      setTerminating(false);
                      return;
                    }
                    await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
                  }
                }

                if (success) {
                  toast.success(`${employee.first_name} reactivated`, { icon: '✅', duration: 3000 });
                  onEmployeeChanged?.();
                  onClose();
                }
                setTerminating(false);
              }}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50"
            >
              {terminating ? 'Reactivating…' : 'Reactivate Employee'}
            </button>
          ) : (
            <button
              type="button"
              disabled={terminating}
              onClick={async () => {
                const reason = prompt(`Termination reason for ${employee.first_name} ${employee.last_name}? (e.g. "Resigned voluntarily", "End of seasonal contract")`);
                if (reason === null) return;
                if (!confirm(`Terminate ${employee.first_name} ${employee.last_name}? This sets termination_date to today and employment_type to terminated. They will appear in the Terminated filter.`)) return;
                setTerminating(true);
                console.log('[Termination] Terminating:', employee.id);

                const updatePayload = {
                  termination_date: new Date().toISOString().slice(0, 10),
                  termination_reason: reason || 'Not specified',
                  employment_type: 'terminated',
                  status: 'offline',
                  updated_at: new Date().toISOString(),
                };

                let success = false;
                for (let attempt = 0; attempt < 3; attempt++) {
                  const timeoutPromise = new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error(`Terminate timed out (attempt ${attempt + 1}/3)`)), 8000));
                  const opPromise = supabase.from('employees').update(updatePayload).eq('id', employee.id)
                    .then((res: any) => { if (res.error) throw res.error; return res.data; });

                  try {
                    await Promise.race([opPromise, timeoutPromise]);
                    console.log(`[Termination] Terminate succeeded attempt ${attempt + 1}`);
                    success = true;
                    break;
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : String(err);
                    const isRateLimit = msg.includes('429') || msg.toLowerCase().includes('too many');
                    const isTimeout = msg.includes('timed out');
                    console.warn(`[Termination] Terminate attempt ${attempt + 1} failed: ${msg}`);
                    if (attempt === 2 || (!isRateLimit && !isTimeout)) {
                      toast.error(`Termination failed: ${msg}`, { duration: 6000 });
                      setTerminating(false);
                      return;
                    }
                    await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
                  }
                }

                if (success) {
                  toast.success(`${employee.first_name} terminated`, { icon: '⚠', duration: 3000 });
                  onEmployeeChanged?.();
                  onClose();
                }
                setTerminating(false);
              }}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
            >
              Terminate Employee
            </button>
          )}
        </div>

        {/* Photo Upload Modal */}
        <PhotoUploadModal
          employee={photoModalOpen ? employee : null}
          onClose={() => setPhotoModalOpen(false)}
          onUploaded={(newUrl) => setResolvedAvatarUrl(newUrl)}
        />

        {/* Document Upload Modal */}
        <DocumentUploadModal
          employee={docModalOpen ? employee : null}
          onClose={() => setDocModalOpen(false)}
          onUploaded={() => {
            supabase
              .from('employee_documents')
              .select('*')
              .eq('employee_id', employee.id)
              .eq('is_archived', false)
              .then(({ data }) => setDocuments((data ?? []) as EmployeeDocument[]));
          }}
        />

        {/* Review Modal */}
        <ReviewModal
          employee={reviewModalState.open ? employee : null}
          existingReview={reviewModalState.existing}
          onClose={() => setReviewModalState({ open: false, existing: null })}
          onSaved={() => {
            supabase
              .from('employee_reviews')
              .select('*')
              .eq('employee_id', employee.id)
              .then(({ data }) => setReviews((data ?? []) as EmployeeFullReview[]));
          }}
        />

        {/* Certification Modal */}
        <CertificationModal
          employee={certModalState.open ? employee : null}
          existingCert={certModalState.existing}
          onClose={() => setCertModalState({ open: false, existing: null })}
          onSaved={() => {
            supabase
              .from('employee_certifications')
              .select('*')
              .eq('employee_id', employee.id)
              .then(({ data }) => setCertifications((data ?? []) as EmployeeFullCertification[]));
          }}
        />

        {/* Emergency Contact Modal */}
        <EmergencyContactModal
          employee={contactModalState.open ? employee : null}
          existingContact={contactModalState.existing}
          onClose={() => setContactModalState({ open: false, existing: null })}
          onSaved={() => {
            supabase.from('employee_emergency_contacts').select('*').eq('employee_id', employee.id)
              .then(({ data }) => setEmergencyContacts((data ?? []) as EmployeeEmergencyContact[]));
          }}
        />

        {/* Time Off Request Modal */}
        <TimeOffRequestModal
          employee={timeOffModalOpen ? employee : null}
          balances={timeOffBalances}
          onClose={() => setTimeOffModalOpen(false)}
          onSaved={() => {
            supabase.from('employee_time_off_balances').select('*').eq('employee_id', employee.id).eq('is_archived', false)
              .then(({ data }) => setTimeOffBalances((data ?? []) as EmployeeTimeOffBalance[]));
          }}
        />

        {/* Balance Allocation Modal */}
        <BalanceAllocationModal
          mode={balanceModalState.mode}
          employee={balanceModalState.mode ? employee : null}
          existingBalance={balanceModalState.existing}
          excludeBalanceTypes={balanceModalState.mode === 'create' ? timeOffBalances.map(b => b.balance_type) : []}
          onClose={() => setBalanceModalState({ mode: null, existing: null })}
          onSaved={() => {
            supabase.from('employee_time_off_balances')
              .select('*')
              .eq('employee_id', employee.id)
              .eq('is_archived', false)
              .then(({ data }) => setTimeOffBalances((data ?? []) as any));
          }}
        />

        {/* Compensation Entry Modal */}
        <CompensationEntryModal
          employee={compensationModalOpen ? employee : null}
          onClose={() => setCompensationModalOpen(false)}
          onSaved={() => {
            supabase.from('employee_compensation_history').select('*').eq('employee_id', employee.id)
              .then(({ data }) => setCompensationHistory((data ?? []) as EmployeeCompensationEntry[]));
          }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 1: OVERVIEW
// ─────────────────────────────────────────────
interface OverviewTabProps {
  employee: EmployeeWithStats;
  editedFields: Partial<Employee>;
  updateField: (key: keyof Employee, value: any) => void;
  resetEditedFields: () => void;
  hasUnsavedChanges: boolean;
  submitting: boolean;
  handleSave: () => void;
  emergencyContacts: EmployeeEmergencyContact[];
  addresses: EmployeeAddress[];
  allEmployees: Employee[];
  onAddContact?: () => void;
  onEditContact?: (ec: EmployeeEmergencyContact) => void;
}

function OverviewTab({
  employee,
  editedFields,
  updateField,
  resetEditedFields,
  hasUnsavedChanges,
  submitting,
  handleSave,
  emergencyContacts,
  addresses,
  allEmployees,
  onAddContact,
  onEditContact,
}: OverviewTabProps) {
  const inputClass =
    'w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all';
  const readonlyClass =
    'px-3 py-2 text-[12px] text-gray-700 rounded-lg bg-gray-50 truncate';

  return (
    <div className="space-y-5">
      {/* Personal Information */}
      <section>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
          Personal Information
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Preferred Name
            </label>
            <input
              type="text"
              value={editedFields.preferred_name ?? employee.preferred_name ?? ''}
              onChange={e => updateField('preferred_name', e.target.value)}
              placeholder="(uses first name)"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Pronouns</label>
            <input
              type="text"
              value={editedFields.preferred_pronouns ?? employee.preferred_pronouns ?? ''}
              onChange={e => updateField('preferred_pronouns', e.target.value)}
              placeholder="e.g. she/her, they/them"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Personal Email
            </label>
            <p className={readonlyClass}>{employee.email ?? '—'}</p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Personal Phone
            </label>
            <p className={readonlyClass}>{employee.phone ?? '—'}</p>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Work Email</label>
            <input
              type="email"
              value={editedFields.work_email ?? employee.work_email ?? ''}
              onChange={e => updateField('work_email', e.target.value)}
              placeholder="user@getnextroof.com"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Work Phone</label>
            <input
              type="tel"
              value={editedFields.work_phone ?? employee.work_phone ?? ''}
              onChange={e => updateField('work_phone', e.target.value)}
              placeholder="(937) 555-0100"
              className={inputClass}
            />
          </div>
        </div>
      </section>

      {/* Employment */}
      <section>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
          Employment
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Job Title</label>
            <input
              type="text"
              value={editedFields.job_title ?? employee.job_title ?? ''}
              onChange={e => updateField('job_title', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Department</label>
            <input
              type="text"
              value={editedFields.department ?? employee.department ?? ''}
              onChange={e => updateField('department', e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">
              Employment Type
            </label>
            <select
              value={editedFields.employment_type ?? employee.employment_type ?? 'full_time'}
              onChange={e => updateField('employment_type', e.target.value)}
              className={inputClass}
            >
              <option value="full_time">Full-Time</option>
              <option value="part_time">Part-Time</option>
              <option value="contractor">Contractor</option>
              <option value="seasonal">Seasonal</option>
              <option value="intern">Intern</option>
              <option value="terminated">Terminated</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Supervisor</label>
            <select
              value={editedFields.supervisor_id ?? employee.supervisor_id ?? ''}
              onChange={e => updateField('supervisor_id', e.target.value || null)}
              className={inputClass}
            >
              <option value="">— None —</option>
              {allEmployees
                .filter(e => e.id !== employee.id)
                .sort((a, b) => a.last_name.localeCompare(b.last_name))
                .map(e => (
                  <option key={e.id} value={e.id}>
                    {e.first_name} {e.last_name} — {e.job_title}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Hire Date</label>
            <p className={readonlyClass}>
              {employee.hire_date
                ? new Date(employee.hire_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '—'}
            </p>
          </div>
          {employee.termination_date && (
            <div>
              <label className="block text-[11px] font-semibold text-red-700 mb-1">
                Termination Date
              </label>
              <p className="px-3 py-2 text-[12px] text-red-700 rounded-lg bg-red-50">
                {new Date(employee.termination_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Bio */}
      <section>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
          Bio
        </label>
        <textarea
          value={editedFields.bio ?? employee.bio ?? ''}
          onChange={e => updateField('bio', e.target.value)}
          placeholder="Short professional bio…"
          rows={3}
          maxLength={1000}
          className="w-full px-3 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none transition-all resize-none"
        />
      </section>

      {/* Emergency Contacts */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Emergency Contacts</h4>
          <button type="button" onClick={onAddContact} className="text-[11px] font-bold text-[#E89A3C] hover:underline">+ Add</button>
        </div>
        {emergencyContacts.length === 0 ? (
          <p className="text-[12px] text-gray-400 italic">No emergency contacts on file.</p>
        ) : (
          <div className="space-y-2">
            {emergencyContacts.map(ec => (
              <button key={ec.id} type="button" onClick={() => onEditContact?.(ec)} className="w-full text-left rounded-lg border border-gray-200 bg-gray-50 hover:border-[#E89A3C] hover:shadow-sm transition-all p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[13px] font-semibold text-[#1C2E5E]">
                      {ec.contact_name}
                      {ec.is_primary && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700">
                          PRIMARY
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500">{ec.relationship}</p>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                  <span className="text-[#E89A3C]">📞 {ec.primary_phone}</span>
                  {ec.email && (
                    <span className="text-[#E89A3C] truncate">✉ {ec.email}</span>
                  )}
                </div>
                {ec.notes && (
                  <p className="text-[11px] text-gray-600 mt-1.5 italic">{ec.notes}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Current Address */}
      <section>
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">
          Current Address
        </h4>
        {(() => {
          const current =
            addresses.find(a => a.is_current && a.address_type === 'home') ?? addresses[0];
          if (!current)
            return <p className="text-[12px] text-gray-400 italic">No address on file.</p>;
          return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-[12px] text-gray-800 leading-relaxed">
                {current.street}
                {current.street2 ? `, ${current.street2}` : ''}
                <br />
                {current.city}, {current.state} {current.zip}
              </p>
            </div>
          );
        })()}
      </section>

      {/* Save row */}
      <section className="pt-3 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-gray-500">
            {hasUnsavedChanges ? 'You have unsaved changes' : 'All changes saved'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => resetEditedFields()}
              disabled={!hasUnsavedChanges || submitting}
              className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasUnsavedChanges || submitting}
              className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-[#E89A3C] hover:bg-[#0891c9] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 2: DOCUMENTS
// ─────────────────────────────────────────────
interface DocumentsTabProps {
  documents: EmployeeDocument[];
  setDocuments: React.Dispatch<React.SetStateAction<EmployeeDocument[]>>;
  supabase: ReturnType<typeof createClient>;
  onUploadClick: () => void;
}

function DocumentsTab({ documents, setDocuments, supabase, onUploadClick }: DocumentsTabProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Documents on File
        </h4>
        <button
          type="button"
          onClick={onUploadClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
        >
          ＋ Upload Document
        </button>
      </div>

      {documents.length === 0 ? (
        <div className="p-8 rounded-lg border border-dashed border-gray-200 text-center">
          <svg
            className="w-10 h-10 mx-auto text-gray-300 mb-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden="true"
          >
            <path
              d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
              strokeLinejoin="round"
            />
            <path d="M14 2v6h6" />
          </svg>
          <p className="text-[13px] font-semibold text-gray-600">No documents yet</p>
          <p className="text-[11px] text-gray-400 mt-1">
            Upload offer letters, certifications, I-9, W-4, and more.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map(doc => (
            <div
              key={doc.id}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-5 h-5 text-blue-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-semibold text-[#1C2E5E] truncate">
                  {doc.display_name}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wide">
                    {doc.document_type.replace(/_/g, ' ')}
                  </span>
                  {doc.expiration_date && (
                    <span
                      className={`text-[10px] font-bold ${
                        new Date(doc.expiration_date).getTime() < Date.now()
                          ? 'text-red-600' :'text-gray-500'
                      }`}
                    >
                      Expires {new Date(doc.expiration_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    const resp = await fetch('/api/hr/document-download-url', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ storage_path: doc.storage_path }),
                    });
                    const data = await resp.json();
                    if (data.signed_url) {
                      window.open(data.signed_url, '_blank');
                    } else {
                      toast.error(data.error ?? 'Download failed');
                    }
                  } catch (err) {
                    toast.error(`Download failed: ${err instanceof Error ? err.message : 'Unknown'}`);
                  }
                }}
                className="text-[#E89A3C] hover:underline text-[11px] font-semibold flex-shrink-0"
              >
                Download
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!confirm(`Delete "${doc.display_name}"? This is soft-delete; the file remains in Storage but is hidden from view.`)) return;
                  const { error } = await supabase
                    .from('employee_documents')
                    .update({ is_archived: true })
                    .eq('id', doc.id);
                  if (error) {
                    toast.error(`Delete failed: ${error.message}`);
                  } else {
                    toast.success('Document removed', { icon: '🗑' });
                    setDocuments(prev => prev.filter(d => d.id !== doc.id));
                  }
                }}
                className="text-red-600 hover:underline text-[11px] font-semibold flex-shrink-0"
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 3: REVIEWS
// ─────────────────────────────────────────────
function ReviewsTab({
  reviews,
  onNewReview,
  onEditReview,
}: {
  reviews: EmployeeFullReview[];
  onNewReview: () => void;
  onEditReview: (rev: EmployeeFullReview) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Performance Reviews
        </h4>
        <button
          type="button"
          onClick={onNewReview}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
        >
          ＋ New Review
        </button>
      </div>

      {reviews.length === 0 ? (
        <p className="text-[12px] text-gray-400 italic">No reviews on file.</p>
      ) : (
        <div className="space-y-3">
          {reviews
            .slice()
            .sort(
              (a, b) =>
                new Date(b.review_period_end).getTime() - new Date(a.review_period_end).getTime()
            )
            .map(rev => {
              const periodLabel = `${new Date(rev.review_period_start).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${new Date(rev.review_period_end).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
              const statusBadge = {
                draft: { bg: 'bg-gray-200', text: 'text-gray-700', label: 'DRAFT' },
                released: { bg: 'bg-green-100', text: 'text-green-700', label: 'RELEASED' },
                acknowledged: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'ACKNOWLEDGED' },
                archived: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'ARCHIVED' },
              }[rev.status] ?? { bg: 'bg-gray-200', text: 'text-gray-700', label: rev.status.toUpperCase() };

              return (
                <button
                  key={rev.id}
                  type="button"
                  onClick={() => onEditReview(rev)}
                  className="w-full text-left rounded-lg border border-gray-200 bg-white p-4 hover:border-[#E89A3C] hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                      <p className="text-[13px] font-bold text-[#1C2E5E]">
                        {rev.review_type
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, c => c.toUpperCase())}{' '}
                        Review
                      </p>
                      <p className="text-[11px] text-gray-500">{periodLabel}</p>
                      {rev.reviewer_name && (
                        <p className="text-[11px] text-gray-500 mt-0.5">By {rev.reviewer_name}</p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${statusBadge.bg} ${statusBadge.text}`}
                    >
                      {statusBadge.label}
                    </span>
                  </div>

                  {typeof rev.overall_rating === 'number' && (
                    <div className="mb-3 pb-3 border-b border-gray-100">
                      <p className="text-[10px] uppercase tracking-wide text-gray-500 font-bold mb-1">
                        Overall
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-[#1C2E5E]">
                          {rev.overall_rating.toFixed(1)}
                        </span>
                        <span className="text-[11px] text-gray-500">/ 5.0</span>
                        <div className="flex items-center gap-0.5 ml-2">
                          {[1, 2, 3, 4, 5].map(i => (
                            <svg
                              key={i}
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill={(rev.overall_rating ?? 0) >= i ? '#F5A623' : '#e5e7eb'}
                              aria-hidden="true"
                            >
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
                    {[
                      { key: 'rating_communication', label: 'Communication' },
                      { key: 'rating_quality_of_work', label: 'Quality of Work' },
                      { key: 'rating_reliability', label: 'Reliability' },
                      { key: 'rating_teamwork', label: 'Teamwork' },
                      { key: 'rating_initiative', label: 'Initiative' },
                      { key: 'rating_safety', label: 'Safety' },
                    ].map(r => {
                      const v = (rev as any)[r.key] as number | null;
                      if (v === null || typeof v !== 'number') return null;
                      return (
                        <div
                          key={r.key}
                          className="flex items-center justify-between rounded bg-gray-50 px-2 py-1"
                        >
                          <span className="text-gray-600">{r.label}</span>
                          <span className="font-bold text-[#1C2E5E]">{v.toFixed(1)}</span>
                        </div>
                      );
                    })}
                  </div>

                  {rev.strengths && (
                    <div className="mb-2">
                      <p className="text-[10px] uppercase tracking-wide text-green-700 font-bold mb-0.5">
                        Strengths
                      </p>
                      <p className="text-[11px] text-gray-700 leading-relaxed">{rev.strengths}</p>
                    </div>
                  )}
                  {rev.areas_for_improvement && (
                    <div className="mb-2">
                      <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold mb-0.5">
                        Areas for Improvement
                      </p>
                      <p className="text-[11px] text-gray-700 leading-relaxed">
                        {rev.areas_for_improvement}
                      </p>
                    </div>
                  )}
                  {rev.goals_next_period && (
                    <div className="mb-2">
                      <p className="text-[10px] uppercase tracking-wide text-blue-700 font-bold mb-0.5">
                        Goals Next Period
                      </p>
                      <p className="text-[11px] text-gray-700 leading-relaxed">
                        {rev.goals_next_period}
                      </p>
                    </div>
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 4: CERTIFICATIONS
// ─────────────────────────────────────────────
function CertificationsTab({
  certifications,
  onNewCert,
  onEditCert,
}: {
  certifications: EmployeeFullCertification[];
  onNewCert: () => void;
  onEditCert: (cert: EmployeeFullCertification) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Certifications
        </h4>
        <button
          type="button"
          onClick={onNewCert}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
        >
          ＋ Add Certification
        </button>
      </div>

      {certifications.length === 0 ? (
        <p className="text-[12px] text-gray-400 italic">No certifications on file.</p>
      ) : (
        <div className="space-y-2">
          {certifications.map(cert => {
            const isExpired = cert.status === 'expired';
            const expiringSoon =
              !!cert.expiration_date &&
              new Date(cert.expiration_date).getTime() < Date.now() + 60 * 24 * 60 * 60 * 1000 &&
              new Date(cert.expiration_date).getTime() > Date.now();

            return (
              <button
                key={cert.id}
                type="button"
                onClick={() => onEditCert(cert)}
                className={`w-full text-left rounded-lg border p-3 hover:shadow-md transition-all ${
                  isExpired
                    ? 'border-red-200 bg-red-50 hover:border-red-400'
                    : expiringSoon
                    ? 'border-amber-200 bg-amber-50 hover:border-amber-400' :'border-gray-200 bg-white hover:border-[#E89A3C]'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-[13px] font-bold text-[#1C2E5E]">
                    {cert.certification_name}
                  </p>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold ${
                      cert.status === 'active' ?'bg-green-100 text-green-700'
                        : cert.status === 'expired' ?'bg-red-100 text-red-700'
                        : cert.status === 'pending' ?'bg-amber-100 text-amber-700' :'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {cert.status.toUpperCase()}
                  </span>
                </div>
                {cert.issuing_organization && (
                  <p className="text-[11px] text-gray-500">{cert.issuing_organization}</p>
                )}
                <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                  {cert.issued_date && (
                    <div>
                      <span className="text-[9px] uppercase tracking-wide text-gray-500 font-bold">
                        Issued
                      </span>
                      <p className="text-gray-700">
                        {new Date(cert.issued_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {cert.expiration_date && (
                    <div>
                      <span className="text-[9px] uppercase tracking-wide text-gray-500 font-bold">
                        Expires
                      </span>
                      <p
                        className={`font-medium ${
                          isExpired
                            ? 'text-red-700'
                            : expiringSoon
                            ? 'text-amber-700' :'text-gray-700'
                        }`}
                      >
                        {new Date(cert.expiration_date).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
                {expiringSoon && (
                  <p className="mt-2 text-[10px] font-bold text-amber-700">
                    ⚠ Expiring within 60 days — schedule renewal
                  </p>
                )}
                {isExpired && (
                  <p className="mt-2 text-[10px] font-bold text-red-700">
                    ⚠ EXPIRED — renew immediately
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 5: TIME OFF
// ─────────────────────────────────────────────
function TimeOffTab({ timeOffBalances, onRequestTimeOff, onEditBalance, onGrantBalance, onAddBalance }: { timeOffBalances: EmployeeTimeOffBalance[]; onRequestTimeOff: () => void; onEditBalance: (bal: EmployeeTimeOffBalance) => void; onGrantBalance: (bal: EmployeeTimeOffBalance) => void; onAddBalance: () => void }) {
  const labelKey: Record<string, { label: string; color: string }> = {
    pto: { label: 'Paid Time Off', color: '#E89A3C' },
    sick: { label: 'Sick Time', color: '#dc2626' },
    bereavement: { label: 'Bereavement', color: '#6b7280' },
    jury_duty: { label: 'Jury Duty', color: '#7c3aed' },
    parental: { label: 'Parental Leave', color: '#16a34a' },
    floating_holiday: { label: 'Floating Holiday', color: '#F5A623' },
  };

  return (
    <div>
      <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">
        Time Off Balances
      </h4>

      {timeOffBalances.length === 0 ? (
        <p className="text-[12px] text-gray-400 italic">No time-off balances configured.</p>
      ) : (
        <div className="space-y-3">
          {timeOffBalances.map(bal => {
            const pctUsed =
              bal.hours_accrued > 0 ? (bal.hours_used / bal.hours_accrued) * 100 : 0;
            const cfg = labelKey[bal.balance_type] ?? {
              label: bal.balance_type,
              color: '#6b7280',
            };

            return (
              <div
                key={bal.id}
                role="button"
                tabIndex={0}
                onClick={() => onEditBalance(bal)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onEditBalance(bal); }}
                className="w-full text-left rounded-lg border border-gray-200 bg-white p-4 hover:border-[#E89A3C] hover:shadow-sm transition-all relative group cursor-pointer"
              >
                <span className="absolute top-2 right-2 text-[9px] text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to edit ⚙
                </span>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[13px] font-bold text-[#1C2E5E]">{cfg.label}</p>
                    <p className="text-[10px] text-gray-500">
                      {bal.hours_per_year
                        ? `${bal.hours_per_year}h/yr accrual`
                        : 'Standard accrual'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className="text-2xl font-bold"
                      style={{ color: cfg.color }}
                    >
                      {bal.hours_remaining.toFixed(1)}
                    </p>
                    <p className="text-[10px] text-gray-500">hours remaining</p>
                  </div>
                </div>

                <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden mb-1">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${Math.min(100, pctUsed)}%`,
                      background: cfg.color,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] text-gray-500">
                  <span>{bal.hours_used.toFixed(1)} used</span>
                  <span>{bal.hours_accrued.toFixed(1)} total</span>
                </div>

                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onGrantBalance(bal); }}
                  className="absolute bottom-2 right-2 px-2 py-0.5 rounded text-[10px] font-bold text-green-700 bg-green-50 hover:bg-green-100 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Grant bonus hours"
                >
                  + Grant
                </button>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={() => onAddBalance()}
        className="w-full mt-3 px-4 py-3 rounded-lg text-[12px] font-bold text-[#E89A3C] bg-blue-50 border border-dashed border-blue-200 hover:bg-blue-100 transition-colors"
      >
        + Add Balance Type
      </button>

      <button
        type="button"
      onClick={() => onRequestTimeOff()}
        className="mt-4 w-full px-3 py-2 rounded-lg text-[12px] font-bold text-white bg-[#1C2E5E] hover:bg-[#022b80] transition-colors"
      >
        Request Time Off
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 6: COMPENSATION
// ─────────────────────────────────────────────
function CompensationTab({
  compensationHistory,
  onAddEntry,
}: {
  compensationHistory: EmployeeCompensationEntry[];
  onAddEntry: () => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Compensation History</h4>
        <button type="button" onClick={onAddEntry} className="text-[11px] font-bold text-[#E89A3C] hover:underline">+ Add Entry</button>
      </div>
      <p className="text-[10px] text-gray-400 italic mb-3">
        Salary band labels only. Actual amounts are managed in your payroll provider.
      </p>

      {compensationHistory.length === 0 ? (
        <p className="text-[12px] text-gray-400 italic">No compensation history on file.</p>
      ) : (
        <div className="space-y-2">
          {compensationHistory
            .slice()
            .sort(
              (a, b) =>
                new Date(b.effective_date).getTime() - new Date(a.effective_date).getTime()
            )
            .map((entry, idx) => (
              <div key={entry.id} className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <p className="text-[13px] font-bold text-[#1C2E5E]">
                      {entry.salary_band ?? 'No band'}
                    </p>
                    <p className="text-[11px] text-gray-500 capitalize">
                      {entry.pay_type.replace(/_/g, ' ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11px] font-semibold text-gray-700">
                      {new Date(entry.effective_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                    {idx === 0 && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700 mt-1">
                        CURRENT
                      </span>
                    )}
                  </div>
                </div>
                {entry.change_reason && (
                  <p className="text-[11px] text-gray-600 mt-1">{entry.change_reason}</p>
                )}
                {entry.approved_by_name && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    Approved by {entry.approved_by_name}
                  </p>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
